import type { Product, ProductVariant } from '../types/product';

interface FilterState {
  productClass: string[];
  categories: string[];
  brands: string[];
  tags: string[];
  colors: string[];
  priceRange: [number, number];
  weightRange: [number, number];
  stockStatus: {
    inStock: boolean;
    outOfStock: boolean;
  };
}

export function filterProducts(products: Product[], filters: FilterState, searchQuery: string): Product[] {
  // Create a new array to hold the filtered products
  const filteredProducts: Product[] = [];
  
  // Skip processing if no products
  if (!products || !Array.isArray(products)) {
    console.log("No products array provided or invalid format");
    return [];
  }
  
  products.forEach(originalProduct => {
    if (!originalProduct || typeof originalProduct !== 'object') {
      console.log("Invalid product object:", originalProduct);
      return;
    }
    
    // Skip if product is missing critical properties
    if (!originalProduct.variants || !Array.isArray(originalProduct.variants)) {
      console.log("Product missing variants array:", originalProduct.id);
      return;
    }
    
    // Skip if the product doesn't match class filter
    if (filters.productClass.length > 0) {
      if (!originalProduct.class) {
        console.log("Product missing class property:", originalProduct.id);
        return;
      }
      if (!filters.productClass.includes(originalProduct.class)) {
        return;
      }
    }
    
    // Skip if the product doesn't match category filter
    if (filters.categories.length > 0) {
      if (!originalProduct.category || !Array.isArray(originalProduct.category)) {
        console.log("Product missing category array:", originalProduct.id);
        return;
      }
      if (!filters.categories.some(cat => originalProduct.category.includes(cat))) {
        return;
      }
    }
    
    // Skip if the product doesn't match brand filter
    if (filters.brands.length > 0) {
      if (!originalProduct.brand) {
        console.log("Product missing brand property:", originalProduct.id);
        return;
      }
      if (!filters.brands.includes(originalProduct.brand.toLowerCase())) {
        return;
      }
    }
    
    // Skip if the product doesn't match tag filter
    if (filters.tags.length > 0) {
      if (!originalProduct.tags || !Array.isArray(originalProduct.tags)) {
        console.log("Product missing tags array:", originalProduct.id);
        return;
      }
      if (!filters.tags.some(tag => originalProduct.tags.includes(tag))) {
        return;
      }
    }
    
    // First create a product copy for modification
    const productCopy = JSON.parse(JSON.stringify(originalProduct)) as Product;
    
    // Create a separate copy of all variants to work with
    let allVariants = [...productCopy.variants];
    
    // Apply non-stock filters first
    // Apply color filter to variants
    if (filters.colors.length > 0) {
      allVariants = allVariants.filter(variant => {
        if (!variant.color) return false;
        return filters.colors.includes(variant.color.toLowerCase());
      });
      
      // Skip if no variants match the color filter
      if (allVariants.length === 0) {
        return;
      }
    }
    
    // Apply price range filter to variants
    const [minPrice, maxPrice] = filters.priceRange;
    allVariants = allVariants.filter(variant => {
      if (typeof variant.price !== 'number') return false;
      const price = variant.discounted_price !== undefined ? variant.discounted_price : variant.price;
      return price >= minPrice && price <= maxPrice;
    });
    
    // Skip if no variants match the price filter
    if (allVariants.length === 0) {
      return;
    }
    
    // Apply weight filter
    const [minWeight, maxWeight] = filters.weightRange;
    // Only apply weight filter if the range is not default
    if (minWeight > 0 || maxWeight < 5) {
      allVariants = allVariants.filter(variant => {
        // Look in product specifications for weight info
        if (productCopy.specifications) {
          const techSpecs = productCopy.specifications.find(spec => 
            spec.title === "TEHNILISED ANDMED"
          );
          
          if (techSpecs && techSpecs.table?.main) {
            const weightRow = techSpecs.table.main.find(row => 
              row.label.toLowerCase() === 'kaal'
            );
            
            if (weightRow) {
              const weightText = weightRow.value.split(' ')[0].replace(',', '.');
              const weightValue = parseFloat(weightText || '0');
              return weightValue >= minWeight && weightValue <= maxWeight;
            }
          }
        }
        return true;  // If no weight specification, include the variant
      });
      
      // Skip if no variants match the weight filter
      if (allVariants.length === 0) {
        return;
      }
    }
    
    // Apply search query filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      
      // First check if any variant directly matches the search
      let variantsMatchingSearch = allVariants.filter(variant => 
        variant.name.toLowerCase().includes(searchLower) ||
        (variant.color && variant.color.toLowerCase().includes(searchLower))
      );
      
      // If no direct variant matches, check if the product itself matches
      if (variantsMatchingSearch.length === 0) {
        const productDescription = productCopy.specifications?.find(spec => spec.title === "TOOTEKIRJELDUS");
        
        const productMatchesSearch = 
          (productDescription?.description && 
           productDescription.description.toLowerCase().includes(searchLower)) ||
          originalProduct.tags.some(tag => tag.toLowerCase().includes(searchLower)) ||
          originalProduct.brand.toLowerCase().includes(searchLower);
        
        // If product matches but no variants match, keep all filtered variants
        if (productMatchesSearch) {
          variantsMatchingSearch = allVariants;
        }
      }
      
      // Update the filtered variants based on search
      allVariants = variantsMatchingSearch;
      
      // Skip if no variants match the search filter
      if (allVariants.length === 0) {
        return;
      }
    }
    
    // Now handle stock status filter - this is the critical part that was wrong before
    let filteredVariants = [...allVariants];

    // Only apply stock filtering if we're not showing both (if both are true, show all variants)
    if (filters.stockStatus.inStock && !filters.stockStatus.outOfStock) {
      // Only show in-stock variants
      filteredVariants = allVariants.filter(v => v.stock > 0);
    } else if (!filters.stockStatus.inStock && filters.stockStatus.outOfStock) {
      // Only show out-of-stock variants
      filteredVariants = allVariants.filter(v => v.stock === 0);
    }
    // If both inStock and outOfStock are true, or both are false, show all variants that passed other filters

    // Skip if no variants match the stock filter
    if (filteredVariants.length === 0) {
      return;
    }

    // Apply discount status filter - similar to stock filtering
    if (filters.discountStatus.hasDiscount && !filters.discountStatus.noDiscount) {
      // Only show variants with discount
      filteredVariants = filteredVariants.filter(v => v.discounted_price !== undefined && v.discounted_price < v.price);
    } else if (!filters.discountStatus.hasDiscount && filters.discountStatus.noDiscount) {
      // Only show variants without discount
      filteredVariants = filteredVariants.filter(v => !v.discounted_price || v.discounted_price >= v.price);
    }
    // If both hasDiscount and noDiscount are true, or both are false, show all variants that passed other filters

    // Skip if no variants match the discount filter
    if (filteredVariants.length === 0) {
      return;
    }

    // Update the product copy with filtered variants
    productCopy.variants = filteredVariants;

    // Add the product with filtered variants to the result
    filteredProducts.push(productCopy);
  });
  
  return filteredProducts;
}

export function sortProducts(products: Product[], sortBy: string): Product[] {
  return [...products].sort((a, b) => {
    const getLowestPrice = (product: Product) => {
      if (!product.variants || product.variants.length === 0) return Infinity;
      return Math.min(...product.variants.map(v => {
        const price = v.discounted_price !== undefined ? v.discounted_price : v.price;
        return isNaN(price) ? Infinity : price;
      }));
    };

    const getHighestDiscount = (product: Product) => {
      if (!product.variants || product.variants.length === 0) return 0;
      return Math.max(...product.variants.map(v => {
        if (v.discounted_price === undefined || v.price === undefined) return 0;
        return ((v.price - v.discounted_price) / v.price) * 100;
      }));
    };

    switch (sortBy) {
      case 'price-asc':
        return getLowestPrice(a) - getLowestPrice(b);
      case 'price-desc':
        return getLowestPrice(b) - getLowestPrice(a);
      case 'discount':
        return getHighestDiscount(b) - getHighestDiscount(a);
      case 'new':
        // Sort new products first - corrected logic
        const aIsNew = a.isNew || a.new || a.variants.some(v => v.isNew || v.new);
        const bIsNew = b.isNew || b.new || b.variants.some(v => v.isNew || v.new);
        if (aIsNew && !bIsNew) return -1; // a (new) comes first
        if (!aIsNew && bIsNew) return 1;  // b (new) comes first
        return 0; // both new or both old, maintain original order
      default:
        return 0;
    }
  });
}

export function extractProductMetadata(products: Product[]) {
  // Map unique colors extracting color_code for display
  const colorCodesMap = new Map<string, string>();
  
  products.forEach(product => {
    if (product.variants) {
      product.variants.forEach(variant => {
        if (variant.color && variant.color_code) {
          colorCodesMap.set(variant.color.toLowerCase(), variant.color_code);
        }
      });
    }
  });

  const uniqueColors = [...colorCodesMap.keys()];
  const colorCodes = Object.fromEntries([...colorCodesMap.entries()]);

  const uniqueBrands = [...new Set(
    products.map(product => product.brand).filter(Boolean)
  )];
  
  const uniqueTags = [...new Set(
    products.flatMap(product => 
      product.tags ? product.tags : []
    )
  )];

  return {
    uniqueColors,
    uniqueBrands,
    uniqueTags,
    colorCodes
  };
}
